/**
 * Когортный P&L: матрица cohort_month × plan — оборот, маржи, кол-во подписок.
 *
 * Источник: `api.cohortPnl(months)` → backend `/api/owner/analytics/cohort-pnl`
 * (агрегация по `to_char(created_at, 'YYYY-MM')` × plan_uuid).
 *
 * Когорта = группа клиентов, купивших впервые подписку в один период (месяц).
 * Помогает увидеть как меняется выручка/маржа по тарифам со временем.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { getWebApp } from '@/lib/telegram';

export const Route = createFileRoute('/cohorts')({ component: Cohorts });

type Months = 3 | 6 | 12;
const MONTH_OPTS: Months[] = [3, 6, 12];

function Cohorts() {
  const navigate = useNavigate();
  const [months, setMonths] = useState<Months>(6);
  const { data: rows, isPending } = useQuery({
    queryKey: ['cohortPnl', months],
    queryFn: () => api.cohortPnl(months),
    placeholderData: keepPreviousData,
  });
  const { data: rates } = useQuery({
    queryKey: ['fxRates'],
    queryFn: () => api.fxRates(),
    placeholderData: keepPreviousData,
  });
  const usdToRub = useMemo(() => {
    const r = (rates ?? []).find((x) => x.code === 'RUB');
    return r?.perRub ?? 100;
  }, [rates]);
  const fmt = (centsUsd: number) =>
    `${(((centsUsd * usdToRub) / 100) | 0).toLocaleString('ru')} ₽`;

  // Группируем по cohort_month, чтобы рисовать секции.
  const byMonth = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        plan_name: string;
        plan_uuid: string;
        subs_count: number;
        partner_margin_minor: number;
        platform_margin_minor: number;
        total_revenue_minor: number;
      }>
    >();
    for (const r of rows ?? []) {
      const arr = map.get(r.cohort_month) ?? [];
      arr.push(r);
      map.set(r.cohort_month, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [rows]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/finance' });
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.hide();
    };
  }, [navigate]);

  return (
    <motion.div initial={false} className="p-4 space-y-4 pb-24">
      <div className="flex items-center gap-2 px-2 pt-2">
        <button
          type="button"
          onClick={() => navigate({ to: '/finance' })}
          className="p-1.5 rounded-lg hover:bg-muted/40 active:scale-95 transition"
          aria-label="Назад к финансам"
        >
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Когорты</h2>
          <p
            className="text-[11px] text-muted-foreground/80"
            title="Когорта — группа подписок, созданных в один календарный месяц. По каждой когорте видна выручка по тарифам."
          >
            Маржа по месяцу первой покупки × тарифу
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 bg-muted/40 p-1 rounded-xl">
        {MONTH_OPTS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMonths(m)}
            className={`py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
              months === m
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {m} мес.
          </button>
        ))}
      </div>

      {isPending && !rows ? (
        <>
          <Skeleton className="h-[120px] rounded-2xl" />
          <Skeleton className="h-[120px] rounded-2xl" />
        </>
      ) : byMonth.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Нет подписок за выбранный период.
          </CardContent>
        </Card>
      ) : (
        byMonth.map(([month, items]) => {
          const totalRevenue = items.reduce((s, r) => s + r.total_revenue_minor, 0);
          const totalPartnerMargin = items.reduce((s, r) => s + r.partner_margin_minor, 0);
          const totalPlatformMargin = items.reduce((s, r) => s + r.platform_margin_minor, 0);
          const totalSubs = items.reduce((s, r) => s + r.subs_count, 0);
          return (
            <Card key={month}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-bold text-lg">{month}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {totalSubs} подп. · {items.length} тариф{items.length === 1 ? '' : 'а'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Оборот
                    </div>
                    <div className="font-bold">{fmt(totalRevenue)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div title="Сколько заработали партнёры за подписки этой когорты.">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Маржа партнёров
                    </div>
                    <div className="font-bold text-muted-foreground">
                      {fmt(totalPartnerMargin)}
                    </div>
                  </div>
                  <div title="Сколько заработала платформа за подписки этой когорты (до комиссий платёжек).">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Маржа платформы
                    </div>
                    <div className="font-bold text-primary">
                      {fmt(totalPlatformMargin)}
                    </div>
                  </div>
                </div>
                <details className="rounded-lg bg-muted/20 px-3 py-2">
                  <summary className="text-[11px] font-semibold text-muted-foreground cursor-pointer">
                    По тарифам ({items.length})
                  </summary>
                  <div className="mt-2 space-y-1.5">
                    {items.map((it) => (
                      <div
                        key={it.plan_uuid}
                        className="flex items-baseline justify-between text-xs"
                      >
                        <div className="truncate">
                          <span className="font-semibold">{it.plan_name}</span>
                          <span className="text-muted-foreground"> · {it.subs_count} подп.</span>
                        </div>
                        <div className="font-semibold whitespace-nowrap">
                          {fmt(it.total_revenue_minor)}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </CardContent>
            </Card>
          );
        })
      )}
    </motion.div>
  );
}
